/**
 * Part Master Event Consumer Hook (INT-005)
 * AS9100D-Compliant Real-time Part Data Synchronization
 * 
 * This hook allows components to subscribe to Part Master events
 * (part_updated, part_deleted, part_created, part_restored) for
 * real-time data synchronization across the Inventory Module.
 * 
 * AS9100D Compliance:
 * - 8.1.2 Configuration Management: Part configuration changes propagated
 * - 8.5.2 Identification & Traceability: Part identification updates
 * 
 * Usage:
 * ```jsx
 * const { 
 *   lastPartEvent, 
 *   affectedParts, 
 *   clearAffectedParts 
 * } = usePartMasterEvents({
 *   onPartUpdated: (hpn) => refetchPart(hpn),
 *   onPartDeleted: (hpn) => handlePartRemoved(hpn),
 *   watchedParts: ['R1C2F9K', 'A3B4C5D']  // Optional: only react to specific parts
 * });
 * ```
 * 
 * @version 1.0.0
 * @date January 2026 (IST)
 * @author Atlas Dev Team - INT-005
 */

import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Hook for consuming Part Master events
 * 
 * @param {Object} options - Hook configuration
 * @param {function} options.onPartUpdated - Callback when a part is updated (receives hpn_compact, change_type, changed_fields)
 * @param {function} options.onPartDeleted - Callback when a part is deleted (receives hpn_compact, reason)
 * @param {function} options.onPartCreated - Callback when a new part is created (receives hpn_compact, description)
 * @param {function} options.onPartRestored - Callback when a part is restored (receives hpn_compact)
 * @param {string[]} options.watchedParts - Optional array of HPN compacts to watch (if empty, watches all)
 * @param {boolean} options.enabled - Whether to enable event listening (default: true)
 * @returns {Object} Event state and methods
 */
export const usePartMasterEvents = ({
  onPartUpdated = null,
  onPartDeleted = null,
  onPartCreated = null,
  onPartRestored = null,
  watchedParts = [],
  enabled = true
} = {}) => {
  const [lastPartEvent, setLastPartEvent] = useState(null);
  const [affectedParts, setAffectedParts] = useState([]);
  const [eventCount, setEventCount] = useState(0);
  
  // Store callbacks in refs to avoid effect dependencies
  const callbacks = useRef({
    onPartUpdated,
    onPartDeleted,
    onPartCreated,
    onPartRestored
  });
  
  // Update refs when callbacks change
  useEffect(() => {
    callbacks.current = {
      onPartUpdated,
      onPartDeleted,
      onPartCreated,
      onPartRestored
    };
  }, [onPartUpdated, onPartDeleted, onPartCreated, onPartRestored]);
  
  // Check if we should react to an event for a specific part
  const shouldReactToPart = useCallback((hpn_compact) => {
    if (!watchedParts || watchedParts.length === 0) {
      return true; // Watch all parts if no filter specified
    }
    return watchedParts.includes(hpn_compact?.toUpperCase());
  }, [watchedParts]);
  
  // Clear affected parts list
  const clearAffectedParts = useCallback(() => {
    setAffectedParts([]);
  }, []);
  
  // Track affected part
  const trackAffectedPart = useCallback((hpn_compact, eventType) => {
    setAffectedParts(prev => {
      const existing = prev.find(p => p.hpn_compact === hpn_compact);
      if (existing) {
        // Update existing entry with latest event
        return prev.map(p => 
          p.hpn_compact === hpn_compact 
            ? { ...p, eventType, timestamp: new Date().toISOString() }
            : p
        );
      }
      // Add new entry
      return [...prev, { hpn_compact, eventType, timestamp: new Date().toISOString() }];
    });
  }, []);
  
  // Event handlers
  useEffect(() => {
    if (!enabled) return;
    
    const handlePartUpdate = (event) => {
      const { hpn_compact, change_type, changed_fields } = event.detail || {};
      
      if (!shouldReactToPart(hpn_compact)) return;
      
      setLastPartEvent({
        type: 'updated',
        hpn_compact,
        change_type,
        changed_fields,
        timestamp: new Date().toISOString()
      });
      setEventCount(c => c + 1);
      trackAffectedPart(hpn_compact, 'updated');
      
      if (callbacks.current.onPartUpdated) {
        callbacks.current.onPartUpdated(hpn_compact, change_type, changed_fields);
      }
    };
    
    const handlePartDelete = (event) => {
      const { hpn_compact, reason } = event.detail || {};
      
      if (!shouldReactToPart(hpn_compact)) return;
      
      setLastPartEvent({
        type: 'deleted',
        hpn_compact,
        reason,
        timestamp: new Date().toISOString()
      });
      setEventCount(c => c + 1);
      trackAffectedPart(hpn_compact, 'deleted');
      
      if (callbacks.current.onPartDeleted) {
        callbacks.current.onPartDeleted(hpn_compact, reason);
      }
    };
    
    const handlePartCreate = (event) => {
      const { hpn_compact, description } = event.detail || {};
      
      // For create events, we might want to react even if not in watched list
      // since the part didn't exist before
      
      setLastPartEvent({
        type: 'created',
        hpn_compact,
        description,
        timestamp: new Date().toISOString()
      });
      setEventCount(c => c + 1);
      trackAffectedPart(hpn_compact, 'created');
      
      if (callbacks.current.onPartCreated) {
        callbacks.current.onPartCreated(hpn_compact, description);
      }
    };
    
    const handlePartRestore = (event) => {
      const { hpn_compact } = event.detail || {};
      
      if (!shouldReactToPart(hpn_compact)) return;
      
      setLastPartEvent({
        type: 'restored',
        hpn_compact,
        timestamp: new Date().toISOString()
      });
      setEventCount(c => c + 1);
      trackAffectedPart(hpn_compact, 'restored');
      
      if (callbacks.current.onPartRestored) {
        callbacks.current.onPartRestored(hpn_compact);
      }
    };
    
    // Register event listeners
    window.addEventListener('part-master-update', handlePartUpdate);
    window.addEventListener('part-master-delete', handlePartDelete);
    window.addEventListener('part-master-create', handlePartCreate);
    window.addEventListener('part-master-restore', handlePartRestore);
    
    // Cleanup
    return () => {
      window.removeEventListener('part-master-update', handlePartUpdate);
      window.removeEventListener('part-master-delete', handlePartDelete);
      window.removeEventListener('part-master-create', handlePartCreate);
      window.removeEventListener('part-master-restore', handlePartRestore);
    };
  }, [enabled, shouldReactToPart, trackAffectedPart]);
  
  return {
    // Last event received
    lastPartEvent,
    
    // List of parts affected by recent events
    affectedParts,
    
    // Total count of events received
    eventCount,
    
    // Methods
    clearAffectedParts,
    
    // Check if a specific part was affected
    isPartAffected: useCallback((hpn_compact) => {
      return affectedParts.some(p => p.hpn_compact === hpn_compact?.toUpperCase());
    }, [affectedParts]),
    
    // Get affected parts by event type
    getAffectedByType: useCallback((eventType) => {
      return affectedParts.filter(p => p.eventType === eventType);
    }, [affectedParts])
  };
};

/**
 * Higher-order component to inject Part Master event handling
 * 
 * Usage:
 * ```jsx
 * export default withPartMasterEvents(MyComponent, {
 *   onPartUpdated: (hpn, component) => component.refetch()
 * });
 * ```
 */
export const withPartMasterEvents = (WrappedComponent, eventConfig = {}) => {
  return function WithPartMasterEventsWrapper(props) {
    const partEvents = usePartMasterEvents(eventConfig);
    return <WrappedComponent {...props} partMasterEvents={partEvents} />;
  };
};

export default usePartMasterEvents;
